import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';

const About = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const skillsData = [
    { name: "Frontend Development", color: "bg-gradient-to-r from-blue-500 to-cyan-500", percentage: 95, icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    )},
    { name: "Backend Development", color: "bg-gradient-to-r from-green-500 to-emerald-500", percentage: 90, icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
      </svg>
    )},
    { name: "Mobile Applications", color: "bg-gradient-to-r from-purple-500 to-pink-500", percentage: 85, icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a1 1 0 001-1V4a1 1 0 00-1-1H8a1 1 0 00-1 1v16a1 1 0 001 1z" />
      </svg>
    )},
    { name: "UI/UX Design", color: "bg-gradient-to-r from-orange-500 to-red-500", percentage: 88, icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4 4 4 0 004-4V5z" />
      </svg>
    )}
  ];

  return (
    <section id="about" className="py-20 relative overflow-hidden" ref={ref}>
      {/* Enhanced Background with Professional Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900/20 to-slate-900" />
      
      {/* Color Accent Overlays */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-green-500/5 rounded-full blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.1),transparent_50%)]" />
        
        {/* Enhanced Professional Graphics */}
        <motion.div
          className="absolute top-20 right-20 w-32 h-32 opacity-10"
          animate={{
            rotate: [0, 360],
          }}
          transition={{
            duration: 60,
            repeat: Infinity,
            ease: "linear"
          }}
        >
          <svg viewBox="0 0 100 100" className="w-full h-full">
            <circle cx="50" cy="50" r="40" fill="none" stroke="rgb(59, 130, 246)" strokeWidth="1"/>
            <circle cx="50" cy="50" r="25" fill="none" stroke="rgb(34, 197, 94)" strokeWidth="1"/>
          </svg>
        </motion.div>

        {/* Enhanced Floating Elements */}
        {[...Array(12)].map((_, i) => (
          <motion.div
            key={i}
            className={`absolute w-1 h-1 rounded-full opacity-30 ${
              i % 4 === 0 ? 'bg-blue-400' : 
              i % 4 === 1 ? 'bg-green-400' : 
              i % 4 === 2 ? 'bg-purple-400' : 'bg-cyan-400'
            }`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -15, 0],
              opacity: [0.3, 0.6, 0.3],
            }}
            transition={{
              duration: Math.random() * 4 + 3,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {/* Enhanced Section Title */}
          <motion.div 
            variants={itemVariants}
            className="mb-16"
          >
            <motion.h2 
              className="text-3xl md:text-4xl font-bold text-white mb-4 relative inline-block"
            >
              {t('about.title')}
              <motion.div
                className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-green-500 rounded-full"
                initial={{ scaleX: 0 }}
                animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
              />
            </motion.h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div variants={itemVariants}>
              <motion.p 
                className="text-lg text-slate-300 mb-6 leading-relaxed"
              >
                {t('about.description1')}
              </motion.p>
              <motion.p 
                className="text-lg text-slate-300 leading-relaxed mb-8"
              >
                {t('about.description2')}
              </motion.p>

              {/* Enhanced Professional Stats */}
              <motion.div
                className="grid grid-cols-2 gap-6"
                variants={containerVariants}
              >
                {[
                  { number: "50+", labelKey: "about.stats.projects", color: "from-blue-500 to-cyan-500", icon: (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  )},
                  { number: "3+", labelKey: "about.stats.experience", color: "from-green-500 to-emerald-500", icon: (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )},
                  { number: "100%", labelKey: "about.stats.clients", color: "from-purple-500 to-pink-500", icon: (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )},
                  { number: "24/7", labelKey: "about.stats.support", color: "from-orange-500 to-red-500", icon: (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                ].map((stat, index) => (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    className="relative p-6 bg-slate-800/50 rounded-lg backdrop-blur-sm border border-slate-700 hover:border-slate-600 transition-colors duration-300 overflow-hidden"
                    whileHover={{ y: -3 }}
                  >
                    {/* Gradient Accent */}
                    <div className={`absolute inset-0 bg-gradient-to-r ${stat.color} opacity-5`} />
                    
                    <div className="relative z-10">
                      <div className="flex items-center mb-3">
                        <div className={`text-transparent bg-gradient-to-r ${stat.color} bg-clip-text mr-2`}>
                          {stat.icon}
                        </div>
                      </div>
                      <div className={`text-2xl font-bold text-transparent bg-gradient-to-r ${stat.color} bg-clip-text mb-2`}>
                        {stat.number}
                      </div>
                      <div className="text-slate-400 text-sm">{t(stat.labelKey)}</div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            {/* Enhanced Skills Visualization */}
            <motion.div 
              variants={itemVariants}
              className="relative"
            >
              <motion.div 
                className="bg-slate-800/60 p-8 rounded-lg backdrop-blur-lg border border-slate-700 shadow-xl relative overflow-hidden"
                whileHover={{ y: -5 }}
                transition={{ duration: 0.3 }}
              >
                {/* Background Accent */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-green-500/5" />
                
                <div className="relative z-10">
                  <div className="flex items-center justify-center mb-6">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-green-500 rounded-lg flex items-center justify-center mr-3">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-white">
                        <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-white">
                      {t('about.skills.title')}
                    </h3>
                  </div>

                  <div className="space-y-6">
                    {skillsData.map((skill, index) => (
                      <motion.div
                        key={skill.name}
                        className="relative"
                        initial={{ opacity: 0, x: -30 }}
                        animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
                        transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-3">
                            <div className="text-slate-400">
                              {skill.icon}
                            </div>
                            <span className="text-slate-300 text-sm font-medium">{skill.name}</span>
                          </div>
                          <span className="text-slate-400 text-sm font-bold">{skill.percentage}%</span>
                        </div>
                        
                        <div className="w-full bg-slate-700 rounded-full h-2 overflow-hidden">
                          <motion.div
                            className={`h-full ${skill.color} rounded-full relative shadow-lg`}
                            initial={{ width: 0 }}
                            animate={isInView ? { width: `${skill.percentage}%` } : { width: 0 }}
                            transition={{ duration: 1.2, delay: 1 + index * 0.1, ease: "easeOut" }}
                          />
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;